import { SendOutlined } from '@ant-design/icons'
import { Button, Card, Col, Flex, Row, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useInvestigationsServiceGetInvestigationTestsEndpoint,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpoint,
  useInvestigationsServiceGetSpecimenEndpoint
} from 'src/api/queries'
import { SpecimenLaboratoryStatus, SpecimenStatus } from 'src/api/requests'
import { PageBreadcrumbs, Text } from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import {
  AddInvestigationModal,
  InvestigationTestsCard,
  TestStatusTag
} from '../components'
import { useSendInvestigationTests } from '../hooks'

export const SpecimenDetailsPage = () => {
  const { t, currentLang } = useLocales()

  const { id } = useParams()

  const [isOpen, setIsOpen] = useState(false)

  const queryParams = {
    specimenIds: [String(id)],
    institutionIds: []
  }

  const { data } = useInvestigationsServiceGetSpecimenEndpoint({
    id: String(id)
  })

  const { data: investigations } =
    useInvestigationsServiceGetListSpecimenInvestigationsEndpoint(queryParams)

  const { sendTest, getSendableTests } = useSendInvestigationTests()

  // All tests across every investigation of this specimen, so we can offer a
  // single "send all results" action instead of one per investigation.
  const { data: specimenTests } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(
      { specimenIds: [String(id)], pageSize: 100 },
      [{ specimenIds: [String(id)] }],
      { enabled: !!id }
    )

  const sendableTests = getSendableTests(specimenTests?.data)

  const onSendAll = () =>
    sendableTests.forEach((test) => test.id && sendTest(test.id))

  // A finalized specimen can no longer receive new investigations or tests.
  const isFinalized =
    data?.laboratoryStatus === SpecimenLaboratoryStatus.REJECTED ||
    data?.status === SpecimenStatus.REJECTED ||
    data?.status === SpecimenStatus.COMPLETED

  const columns: ColumnsType = [
    {
      title: t('specimens.code'),
      render: (_, { prefix }) => `${data?.code}.${prefix}`,
      key: 'code'
    },
    {
      title: t('specimens.type'),
      render: (_, { type }) => type?.translation?.[currentLang?.value],
      key: 'type'
    },
    {
      title: t('specimens.category'),
      render: (_, { category }) => category?.translation?.[currentLang?.value],
      key: 'category'
    },
    {
      title: t('form.status'),
      render: (_, { specimenStatus }) => (
        <TestStatusTag status={specimenStatus} />
      ),
      key: 'status'
    }
  ]

  const onToggle = () => setIsOpen((prev) => !prev)

  const pageTitle = `${t('tests.specimen_details')} ${data?.laboratoryInternalCode ?? ''}`

  return (
    <PageBreadcrumbs
      title={pageTitle}
      items={[{ title: pageTitle }]}
      extraProps={{ align: 'flex-end' }}
    >
      <Flex vertical gap={32}>
        <Card>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Text>
                {t('specimens.created_by')}:{' '}
                <Text weight={400}>
                  {getFullName(
                    data?.createdByUser?.lastName,
                    data?.createdByUser?.firstName
                  )}
                </Text>
              </Text>
            </Col>

            <Col span={12}>
              <Text>
                {t('specimens.created_at')}:{' '}
                <Text weight={400}>
                  {dayjs
                    .utc(data?.createdAt)
                    .tz()
                    .format(dayMonthYearHourMinutesFormatWithDot)}
                </Text>
              </Text>
            </Col>
            <Col span={12}>
              <Text>
                {t('specimens.harvested_by')}:{' '}
                <Text weight={400}>
                  {data?.harvestedByUser
                    ? getFullName(
                        data?.harvestedByUser?.lastName,
                        data?.harvestedByUser?.firstName
                      )
                    : '-'}
                </Text>
              </Text>
            </Col>

            <Col span={12}>
              <Text>
                {t('specimens.harvested_at')}:{' '}
                <Text weight={400}>
                  {dayjs
                    .utc(data?.harvestedAt)
                    .tz()
                    .format(dayMonthYearHourMinutesFormatWithDot)}
                </Text>
              </Text>
            </Col>

            <Col span={12}>
              <Text>
                {t('specimens.received_by')}:{' '}
                <Text weight={400}>
                  {data?.receivedByUser
                    ? getFullName(
                        data?.receivedByUser?.lastName,
                        data?.receivedByUser?.firstName
                      )
                    : '-'}
                </Text>
              </Text>
            </Col>

            <Col span={12}>
              <Text>
                {t('specimens.received_at')}:{' '}
                <Text weight={400}>
                  {data?.receivedAt
                    ? dayjs
                        .utc(data?.receivedAt)
                        .tz()
                        .format(dayMonthYearHourMinutesFormatWithDot)
                    : '-'}
                </Text>
              </Text>
            </Col>

            <Col span={12}>
              <Text>
                {t('specimens.person')}:{' '}
                <Text weight={400}>
                  {getFullName(
                    String(data?.person?.lastName),
                    String(data?.person?.firstName)
                  )}
                </Text>
              </Text>
            </Col>

            <Col span={12}>
              <Text>
                {t('specimens.specimen_type')}:{' '}
                <Text weight={400}>
                  {data?.type?.translation?.[currentLang?.value]}
                </Text>
              </Text>
            </Col>

            <Col span={24}>
              <Flex vertical gap={4}>
                <Text>{t('common.comments')}: </Text>
                {data?.comments?.map((comment, index) => (
                  <Text key={comment.id} weight={400}>
                    {index + 1}. {comment.text} -{' '}
                    {getFullName(
                      comment.createdByUser?.lastName,
                      comment.createdByUser?.firstName
                    )}
                  </Text>
                ))}
              </Flex>
            </Col>
          </Row>
        </Card>

        <Table
          rowKey="id"
          dataSource={investigations?.data}
          pagination={false}
          title={() =>
            (!isFinalized || !!sendableTests.length) && (
              <Flex justify="flex-end" gap={8}>
                {!!sendableTests.length && (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={onSendAll}
                  >
                    {t('investigations.send_all')}
                  </Button>
                )}

                {!isFinalized && (
                  <Button onClick={onToggle}>
                    {t('specimens.add_investigation')}
                  </Button>
                )}
              </Flex>
            )
          }
          columns={columns}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowRender: (record) => (
              <InvestigationTestsCard
                investigationId={record.id}
                typeId={record.type?.id}
                specimenTypeId={data?.type?.id}
                isFinalized={isFinalized}
              />
            )
          }}
        />

        {isOpen && (
          <AddInvestigationModal
            specimenId={data?.type?.id}
            existingTypeIds={(investigations?.data ?? [])
              .map((investigation) => investigation.type?.id)
              .filter((typeId): typeId is string => !!typeId)}
            onCancel={onToggle}
          />
        )}
      </Flex>
    </PageBreadcrumbs>
  )
}
